// §5.1 / §5.3 — the window store.
//
// Authoritative geometry lives here, and **during a pointer drag it is not updated** (§5.2) —
// `drag.ts` writes to the DOM and commits here once on `pointerup`.
//
// `z` is a dense integer reassigned on every focus change; DOM order is never used for
// stacking (§5.7), so the host layer can stay append-only.
import { createStore, type StoreApi } from 'zustand/vanilla';
import { nanoid } from 'nanoid';
import type { Desktop, Rect, Win, WinState } from '@hangar/shared';
import type { Bus } from '../bus';
import { keepReachable, nextRestoreRect, rectsEqual, resolveRestore } from './geometry';

export const DEFAULT_DESKTOP_ID = 'desktop-1';
export const DEFAULT_MIN_W = 240;
export const DEFAULT_MIN_H = 160;

export interface WmState {
  windows: Record<string, Win>;
  desktops: Desktop[];
  activeDesktopId: string;
  focusedId: string | null;
}

export interface CreateWinInput {
  appId: string;
  rect: Rect;
  title?: string;
  iconSvg?: string;
  minW?: number;
  minH?: number;
  resizable?: boolean;
  desktopId?: string;
  /** Supplied by session restore (§5.6) so a restored window keeps its identity. */
  id?: string;
  instanceKey?: string;
  state?: WinState;
  z?: number;
  focus?: boolean;
}

export interface WmStoreOptions {
  /** Injected so tests get deterministic ids and timestamps. */
  newId?: () => string;
  now?: () => number;
}

export interface WmStore {
  readonly store: StoreApi<WmState>;
  getState(): WmState;
  get(id: string): Win | undefined;
  list(): Win[];
  /** Windows on one desktop, in ascending z. */
  listOnDesktop(desktopId: string): Win[];
  create(input: CreateWinInput): Win;
  close(id: string): void;
  focus(id: string): void;
  /** Geometry commit — one call per drag, not one per pointermove (§5.2). */
  setRect(id: string, rect: Rect): void;
  /** Like `setRect` but clamps to the window's `minW`/`minH`. */
  resize(id: string, rect: Rect): void;
  setWinState(id: string, state: WinState): void;
  maximize(id: string, workArea: Rect): void;
  minimize(id: string): void;
  /** With an `area` the restored rect is clamped back into view; without it, exact (§5.2). */
  restore(id: string, area?: Rect): void;
  setSnap(id: string, snap: Win['snap'], rect: Rect): void;
  /** §5.6 — session restore putting back the pre-snap rect `setSnap` could not have known. */
  setRestoreRect(id: string, rect: Rect | null): void;
  /**
   * §5.2 (D-438) — pull every floating window back to where it can be grabbed.
   *
   * Called whenever the work area changes, which includes boot. It resizes nothing and moves only
   * what has become unreachable, so an ordinary browser resize disturbs no layout — and a window
   * that was already stranded above the top of the screen, from a session saved before the drag
   * bug was fixed, comes back the moment the desktop loads.
   */
  keepWindowsReachable(area: Rect): void;
  setChrome(id: string, patch: Partial<Pick<Win, 'title' | 'iconSvg' | 'dirty' | 'busy'>>): void;
  moveToDesktop(id: string, desktopId: string): void;
  setActiveDesktop(desktopId: string): void;
  setDesktops(desktops: Desktop[]): void;
}

function initialState(): WmState {
  return {
    windows: {},
    desktops: [{ id: DEFAULT_DESKTOP_ID, name: 'Desktop 1', order: 0 }],
    activeDesktopId: DEFAULT_DESKTOP_ID,
    focusedId: null,
  };
}

/** Reassigns `z` to 0..n-1 in the given order, leaving every other field untouched. */
function densify(windows: Record<string, Win>, orderedIds: string[]): Record<string, Win> {
  const next: Record<string, Win> = { ...windows };
  orderedIds.forEach((id, index) => {
    const win = next[id];
    if (win && win.z !== index) next[id] = { ...win, z: index };
  });
  return next;
}

function byZ(windows: Record<string, Win>): Win[] {
  return Object.values(windows).sort((a, b) => a.z - b.z);
}

export function createWmStore(bus: Bus, options: WmStoreOptions = {}): WmStore {
  const newId = options.newId ?? (() => nanoid(10));
  const now = options.now ?? (() => Date.now());
  const store = createStore<WmState>(() => initialState());

  const patch = (id: string, mutate: (win: Win) => Win): Win | undefined => {
    const win = store.getState().windows[id];
    if (!win) return undefined;
    const next = mutate(win);
    store.setState((s) => ({ windows: { ...s.windows, [id]: next } }));
    return next;
  };

  const api: WmStore = {
    store,

    getState() {
      return store.getState();
    },

    get(id) {
      return store.getState().windows[id];
    },

    list() {
      return byZ(store.getState().windows);
    },

    listOnDesktop(desktopId) {
      return byZ(store.getState().windows).filter((w) => w.desktopId === desktopId);
    },

    create(input) {
      const id = input.id ?? newId();
      const at = now();
      const win: Win = {
        id,
        appId: input.appId,
        instanceKey: input.instanceKey ?? `${input.appId}:${id}`,
        title: input.title ?? input.appId,
        iconSvg: input.iconSvg ?? '',
        dirty: false,
        busy: false,
        rect: { ...input.rect },
        restoreRect: null,
        state: input.state ?? 'normal',
        snap: null,
        desktopId: input.desktopId ?? store.getState().activeDesktopId,
        z: input.z ?? Object.keys(store.getState().windows).length,
        minW: input.minW ?? DEFAULT_MIN_W,
        minH: input.minH ?? DEFAULT_MIN_H,
        resizable: input.resizable ?? true,
        createdAt: at,
        focusedAt: at,
      };

      store.setState((s) => ({ windows: { ...s.windows, [id]: win } }));
      bus.emit('window:created', { id, appId: win.appId, instanceKey: win.instanceKey });
      if (input.focus !== false) api.focus(id);
      return store.getState().windows[id] ?? win;
    },

    close(id) {
      const win = store.getState().windows[id];
      if (!win) return;

      store.setState((s) => {
        const windows = { ...s.windows };
        delete windows[id];
        const ordered = byZ(windows).map((w) => w.id);
        return { windows: densify(windows, ordered) };
      });

      bus.emit('window:closed', { id, appId: win.appId, instanceKey: win.instanceKey });

      if (store.getState().focusedId === id) {
        // §5.3 — the next window by most-recent focus takes over.
        const candidates = Object.values(store.getState().windows)
          .filter((w) => w.state !== 'minimized')
          .sort((a, b) => b.focusedAt - a.focusedAt);
        const next = candidates[0];
        if (next) api.focus(next.id);
        else {
          store.setState({ focusedId: null });
          bus.emit('window:focused', { id: null });
        }
      }
    },

    focus(id) {
      const state = store.getState();
      if (!state.windows[id]) return;

      const ordered = byZ(state.windows)
        .map((w) => w.id)
        .filter((other) => other !== id);
      ordered.push(id);

      const windows = densify(state.windows, ordered);
      const target = windows[id];
      if (target) windows[id] = { ...target, focusedAt: now() };

      store.setState({ windows, focusedId: id });
      bus.emit('window:focused', { id });
    },

    setRect(id, rect) {
      const next = patch(id, (win) => ({ ...win, rect: { ...rect } }));
      if (next) bus.emit('window:moved', { id, rect: next.rect });
    },

    resize(id, rect) {
      const win = store.getState().windows[id];
      if (!win) return;
      const clamped: Rect = {
        x: rect.x,
        y: rect.y,
        w: Math.max(rect.w, win.minW),
        h: Math.max(rect.h, win.minH),
      };
      api.setRect(id, clamped);
    },

    keepWindowsReachable(area) {
      const current = store.getState().windows;
      const windows: Record<string, Win> = {};
      const moved: { id: string; rect: Rect }[] = [];
      for (const [id, win] of Object.entries(current)) {
        // A maximized or snapped window's rect belongs to its zone rather than to the player, and
        // the skin re-applies it; moving one here would fight whoever owns it.
        if (win.state === 'maximized' || win.state === 'snapped') {
          windows[id] = win;
          continue;
        }
        const rect = keepReachable(win.rect, area);
        if (rectsEqual(rect, win.rect)) {
          windows[id] = win;
          continue;
        }
        windows[id] = { ...win, rect };
        moved.push({ id, rect });
      }
      if (moved.length === 0) return;
      store.setState({ windows });
      for (const entry of moved) bus.emit('window:moved', { id: entry.id, rect: { ...entry.rect } });
    },

    setWinState(id, state) {
      const next = patch(id, (win) => ({ ...win, state }));
      if (next) bus.emit('window:stateChanged', { id, state });
    },

    maximize(id, workArea) {
      const win = store.getState().windows[id];
      if (!win || win.state === 'maximized') return;
      patch(id, (w) => ({
        ...w,
        restoreRect: nextRestoreRect(w.rect, w.restoreRect),
        rect: { ...workArea },
        state: 'maximized',
        snap: null,
      }));
      bus.emit('window:stateChanged', { id, state: 'maximized' });
      bus.emit('window:moved', { id, rect: { ...workArea } });
    },

    minimize(id) {
      const win = store.getState().windows[id];
      if (!win || win.state === 'minimized') return;
      patch(id, (w) => ({ ...w, state: 'minimized' }));
      bus.emit('window:stateChanged', { id, state: 'minimized' });

      if (store.getState().focusedId === id) {
        const next = Object.values(store.getState().windows)
          .filter((w) => w.id !== id && w.state !== 'minimized')
          .sort((a, b) => b.focusedAt - a.focusedAt)[0];
        if (next) api.focus(next.id);
        else {
          store.setState({ focusedId: null });
          bus.emit('window:focused', { id: null });
        }
      }
    },

    restore(id, area) {
      const win = store.getState().windows[id];
      if (!win) return;
      // A minimized window returns to whatever it was before; anything else returns to its
      // pre-maximize / pre-snap rect.
      if (win.state === 'minimized') {
        patch(id, (w) => ({ ...w, state: w.snap ? 'snapped' : w.restoreRect ? 'maximized' : 'normal' }));
        const restored = store.getState().windows[id];
        if (restored) bus.emit('window:stateChanged', { id, state: restored.state });
        return;
      }
      const rect = resolveRestore(win.rect, win.restoreRect, area, { w: win.minW, h: win.minH });
      const moved = win.restoreRect !== null || area !== undefined;
      patch(id, (w) => ({ ...w, rect, restoreRect: null, state: 'normal', snap: null }));
      bus.emit('window:stateChanged', { id, state: 'normal' });
      if (moved) bus.emit('window:moved', { id, rect });
    },

    setSnap(id, snap, rect) {
      const win = store.getState().windows[id];
      if (!win) return;
      patch(id, (w) => ({
        ...w,
        // Keep the first pre-snap rect: snapping left then right must still restore to the
        // rect the window had before any of it started.
        restoreRect: nextRestoreRect(w.rect, w.restoreRect),
        rect: { ...rect },
        snap,
        state: snap ? 'snapped' : 'normal',
      }));
      bus.emit('window:stateChanged', { id, state: snap ? 'snapped' : 'normal' });
      bus.emit('window:moved', { id, rect: { ...rect } });
    },

    setRestoreRect(id, rect) {
      patch(id, (w) => ({ ...w, restoreRect: rect ? { ...rect } : null }));
    },

    setChrome(id, chrome) {
      const next = patch(id, (win) => ({ ...win, ...chrome }));
      if (next) bus.emit('window:chromeChanged', { id });
    },

    moveToDesktop(id, desktopId) {
      const win = store.getState().windows[id];
      if (!win || win.desktopId === desktopId) return;
      patch(id, (w) => ({ ...w, desktopId }));
      bus.emit('window:desktopChanged', { id, desktopId });
    },

    setActiveDesktop(desktopId) {
      if (store.getState().activeDesktopId === desktopId) return;
      store.setState({ activeDesktopId: desktopId });
      bus.emit('window:desktopsChanged', { activeDesktopId: desktopId });
    },

    setDesktops(desktops) {
      store.setState({ desktops: [...desktops] });
      bus.emit('window:desktopsChanged', { activeDesktopId: store.getState().activeDesktopId });
    },
  };

  return api;
}
